import { useState, useEffect } from 'react'
import type { DeliveryAddress } from '@webmono/storefront-ui/types'
import { cn } from '@webmono/storefront-ui/utils'

interface AddressFormProps {
  value?: DeliveryAddress
  onChange: (address: DeliveryAddress | undefined) => void
}

export function AddressForm({ value, onChange }: AddressFormProps) {
  const [street, setStreet] = useState(value?.street || '')
  const [unit, setUnit] = useState(value?.unit || '')
  const [city, setCity] = useState(value?.city || '')
  const [province, setProvince] = useState(value?.province || '')
  const [postalCode, setPostalCode] = useState(value?.postalCode || '')
  const [instructions, setInstructions] = useState(value?.instructions || '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (street && city && province && postalCode) {
      const address: DeliveryAddress = {
        street,
        unit: unit || undefined,
        city,
        province,
        postalCode,
        country: 'CA',
        instructions: instructions || undefined,
      }
      onChange(address)
    } else if (!street && !city && !province && !postalCode) {
      onChange(undefined)
    }
  }, [street, unit, city, province, postalCode, instructions, onChange])

  const validatePostalCode = (code: string): boolean => {
    const canadianPostal = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/
    return canadianPostal.test(code)
  }

  const handlePostalCodeChange = (value: string) => {
    const formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (formatted.length <= 6) {
      const display = formatted.length > 3
        ? `${formatted.slice(0, 3)} ${formatted.slice(3)}`
        : formatted
      setPostalCode(display)

      if (formatted.length === 6 && !validatePostalCode(display)) {
        setErrors((prev) => ({ ...prev, postalCode: 'Invalid postal code' }))
      } else {
        setErrors((prev) => {
          const { postalCode: _, ...rest } = prev
          return rest
        })
      }
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <input
          type="text"
          placeholder="Street address"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div>
        <input
          type="text"
          placeholder="Apt, suite, unit (optional)"
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <input
          type="text"
          placeholder="City"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />

        <select
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          className={cn(
            'w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
            !province && 'text-muted-foreground'
          )}
        >
          <option value="">Province</option>
          <option value="AB">Alberta</option>
          <option value="BC">British Columbia</option>
          <option value="MB">Manitoba</option>
          <option value="NB">New Brunswick</option>
          <option value="NL">Newfoundland</option>
          <option value="NS">Nova Scotia</option>
          <option value="NT">Northwest Territories</option>
          <option value="NU">Nunavut</option>
          <option value="ON">Ontario</option>
          <option value="PE">Prince Edward Island</option>
          <option value="QC">Quebec</option>
          <option value="SK">Saskatchewan</option>
          <option value="YT">Yukon</option>
        </select>
      </div>

      <div>
        <input
          type="text"
          placeholder="Postal code (e.g., M5V 3L9)"
          value={postalCode}
          onChange={(e) => handlePostalCodeChange(e.target.value)}
          className={cn(
            'w-full px-4 py-3 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring',
            errors.postalCode ? 'border-destructive' : 'border-input'
          )}
        />
        {errors.postalCode && (
          <p className="text-xs text-destructive mt-1">{errors.postalCode}</p>
        )}
      </div>

      <div>
        <textarea
          placeholder="Delivery instructions (optional)"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={2}
          className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
        />
        <p className="text-xs text-muted-foreground mt-1">
          e.g., "Ring buzzer 123", "Leave at door", "Call on arrival"
        </p>
      </div>
    </div>
  )
}
